import axios from "axios";
import useReaderStore from "../context/readerStore";
import { clearAiSessionCaches } from "./aiService";
import { showSessionExpiredNotification } from "./notificationService";

/**
 * Global axios response interceptor to handle token/session related errors.
 * - Detects `rst_token_expired` code from API responses
 * - Detects HTTP 401 responses
 * - Clears sessionStorage, notifies user, and forces the reader to EmptyState
 *
 * This module runs its setup on import. Import it once at application
 * startup (for example in `src/App.js`) to enable global handling.
 */
const setupApiInterceptor = () => {
  const handleAuthError = (data, status) => {
    try {
      const code = data && typeof data === "object" ? data.code || (data.error && data.error.code) : null;
      const reason = data && typeof data === "object" ? data.reason || (data.error && data.error.reason) : null;

      const isRstExpired = code === "rst_token_expired";
      const isTokenExpiredReason = reason === "token_expired";
      const isUnauthorized = status === 401;

      if (isRstExpired || isTokenExpiredReason || isUnauthorized) {
        try {
          sessionStorage.removeItem("igp_session");
        } catch (e) {
          // ignore
        }

        try {
          clearAiSessionCaches();
        } catch (e) {
          // ignore
        }

        try {
          showSessionExpiredNotification();
        } catch (e) {
          // ignore notification errors
        }

        try {
          const store = useReaderStore.getState();
          if (store?.setDocument) {
            store.setDocument(null, null, "", {});
          }
          if (store?.setError) {
            store.setError(
              "RST token has expired. Please request a new access link.",
            );
          }
        } catch (e) {
          // ignore store errors
        }
        return true;
      }
    } catch (e) {
      console.error("apiInterceptor handler failed", e);
    }
    return false;
  };

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const resp = error?.response;
      handleAuthError(resp?.data, resp?.status);
      return Promise.reject(error);
    },
  );
};

setupApiInterceptor();

export default setupApiInterceptor;
